import { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { requestAccess } from '../../services/access-request/index.tsx';
import { fireErrorAnalytics } from '../../services/analytics/index.tsx';
import { useFilteredUsersFromCollaborationGraph } from '../../services/get-collaborators/index.tsx';
import { useMsrRequestAccessErrorFlag } from '../request-access-error-flag/msr-request-access-error-flag.tsx';
import { useMsrRequestAccessSuccessFlag } from '../request-access-success-flag/msr-request-access-success-flag.tsx';
import {
	getAnalyticsAttributes,
	useMsrRequestAccessPromptFlag,
} from '../request-access-prompt-flag/msr-request-access-flag.tsx';

const MAX_NUMBER_OF_JPD_COLLABORATORS = 10;

export const useJpdMsrCrossJoinNudge = () => {
	const cloudId = useCloudId();
	const userId = useAccountId() || '';
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const getFilteredCollaborators = useFilteredUsersFromCollaborationGraph(
		cloudId,
		'jira-product-discovery',
		MAX_NUMBER_OF_JPD_COLLABORATORS,
	);
	const showMsrRequestAccessPromptFlag = useMsrRequestAccessPromptFlag();
	const showSuccessFlag = useMsrRequestAccessSuccessFlag();
	const showErrorFlag = useMsrRequestAccessErrorFlag();
	const { push } = useRouterActions();

	return useCallback(async () => {
		try {
			const startTimestamp = Date.now();
			const collaboratorsOnJpd = await getFilteredCollaborators();
			const onSubmit = async () => {
				try {
					const accessStatus = await requestAccess(
						'jira-product-discovery',
						cloudId,
						userId,
						'crossJoinNudgesJiraToJpd',
						createAnalyticsEvent,
					);
					if (
						accessStatus === 'ACCESS_GRANTED' ||
						accessStatus === 'ACCESS_EXISTS' ||
						accessStatus === 'APPROVED_REQUEST_EXISTS'
					) {
						push('/jira/projects');
					} else if (
						accessStatus === 'PENDING_REQUEST_CREATED' ||
						accessStatus === 'PENDING_REQUEST_EXISTS'
					) {
						showSuccessFlag();
					} else {
						showErrorFlag('jira-product-discovery');
					}
				} catch {
					showErrorFlag('jira-product-discovery');
				}
			};

			showMsrRequestAccessPromptFlag({
				users: collaboratorsOnJpd,
				onSubmit,
				targetProduct: 'jira-product-discovery',
				integration: 'crossJoinNudgesJiraToJpd',
				eventAttributes: getAnalyticsAttributes({
					userId,
					users: collaboratorsOnJpd,
					startTimestamp,
				}),
			});

			// TODO: fire operational analytics for MSR JPD

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics('useJpdMsrCrossJoinNudge', error, { cloudId });
		}
	}, [
		cloudId,
		createAnalyticsEvent,
		getFilteredCollaborators,
		push,
		showErrorFlag,
		showMsrRequestAccessPromptFlag,
		showSuccessFlag,
		userId,
	]);
};
